/** Organizational live queries: jurusan, prodi, and membership scoping. */

import { useMemo } from 'react';
import { COL } from '../../collections';
import { where, orderBy } from '../../collections';
import { useLiveQuery } from '../hooks';
import type { JurusanDoc, ProdiDoc, UserDoc } from '../../types';

/** All jurusan (departments), active first. */
export function useAllJurusan() {
  return useLiveQuery<JurusanDoc>(COL.jurusan, [orderBy('name', 'asc')]);
}

/** All program studi. */
export function useAllProdi() {
  return useLiveQuery<ProdiDoc>(COL.prodi, [orderBy('name', 'asc')]);
}

/** Prodi belonging to a jurusan. */
export function useProdiByJurusan(jurusanId: string | null) {
  return useLiveQuery<ProdiDoc>(
    jurusanId ? COL.prodi : null,
    jurusanId ? [where('jurusanId', '==', jurusanId), orderBy('name', 'asc')] : [],
    Boolean(jurusanId),
  );
}

/** Active prodi a dosen/staf_jurusan belongs to (resolved from user.prodiIds). */
export function useProdiByIds(prodiIds: string[]) {
  const enabled = prodiIds.length > 0;
  const { data } = useLiveQuery<ProdiDoc>(enabled ? COL.prodi : null, enabled ? [] : [], enabled);
  const byId = useMemo(() => new Map(data.map((p) => [p.id, p])), [data]);
  return useMemo(
    () => prodiIds.map((id) => byId.get(id)).filter((p): p is ProdiDoc => Boolean(p)),
    [prodiIds, byId],
  );
}

/** Dosens that belong to a given prodi (by user.prodiIds). */
export function useDosenUsersByProdi(prodiId: string | null) {
  const { data } = useLiveQuery<UserDoc>(COL.users, [
    where('role', '==', 'dosen'),
    orderBy('name', 'asc'),
  ]);
  const filtered = useMemo(
    () => data.filter((u) => u.status === 'active' && (u.prodiIds ?? []).includes(prodiId ?? '')),
    [data, prodiId],
  );
  return { data: filtered, loading: false };
}

/** Active prodi of the current prodi-manager user (dosen kaprodi / staf_jurusan). */
export function useMyManagedProdi() {
  const all = useAllProdi().data;
  return all.filter((p) => p.status === 'active');
}
